import { isNativeApp } from './native/platform'
import { clearCredential, getCredential, setCredential } from './native/credentialStore'
import { publicOrigin } from './native/instance'

const RETURN_KEY = 'canvasser_return'

/**
 * [L11 P2] A device-local re-entry credential for a signed-out self-enrollee: the durable
 * bootstrap TOKEN (redeemed via POST /api/grant/redeem) plus its grantId. Deliberate, documented
 * exception to the ids-only grant mirror (grantMirror.ts) — this holds a bearer token, never a
 * name/address/PII. In the wipe set: cleared on explicit logout (useGrantSession.logout) and on a
 * genuine redeem-404 self-heal (RedeemPage). NOT cleared on ordinary session expiry — that is the
 * case re-entry exists to serve.
 *
 * [Native B1, NB1-12] On native the same value lives in OS secure storage (credentialStore kind
 * 'canvasserReturn', namespaced by instance), never localStorage. Web is unchanged.
 */
export interface ReturnCredential {
  token: string
  grantId: string
}

export function getReturnCredential(): ReturnCredential | null {
  const raw = isNativeApp() ? getCredential('canvasserReturn') : localStorage.getItem(RETURN_KEY)
  if (!raw) return null
  try {
    const p = JSON.parse(raw)
    if (typeof p?.token !== 'string' || typeof p?.grantId !== 'string') return null
    return { token: p.token, grantId: p.grantId }
  } catch {
    return null
  }
}

export function setReturnCredential(c: ReturnCredential): void {
  const value = JSON.stringify({ token: c.token, grantId: c.grantId })
  if (isNativeApp()) {
    // Async secure-storage write; a failure just means re-entry is unavailable on this device
    // (the same outcome as a localStorage quota error on web, minus the throw).
    void setCredential('canvasserReturn', value).catch(() => {})
    return
  }
  localStorage.setItem(RETURN_KEY, value)
}

export function clearReturnCredential(): void {
  // Native: the in-memory copy is dropped synchronously, so the wipe is immediately visible.
  if (isNativeApp()) {
    void clearCredential('canvasserReturn').catch(() => {})
    return
  }
  localStorage.removeItem(RETURN_KEY)
}

/**
 * The durable re-entry link for a return token (JoinPage's "save your way back" + the profile).
 * Defaults to `publicOrigin()` — on native the instance, never `capacitor://localhost`; null there
 * with no instance, and callers then show no link.
 */
export function returnLinkFor(token: string, origin: string | null = publicOrigin()): string | null {
  return origin ? `${origin}/canvasser/redeem?token=${token}` : null
}
